/**
 * Lê o essencial do XML de uma nota para preencher o registro: número, série, chave, data, valor e
 * as partes. Cobre NF-e (produto), NFS-e do padrão nacional e NFS-e ABRASF (a maioria das
 * prefeituras). Sem DOM, só expressões: roda igual no navegador e no teste.
 */

export type DadosDaNota = {
  modelo: "NFE" | "NFSE";
  numero: string | null;
  serie: string | null;
  chaveDeAcesso: string | null;
  emitidaEm: string | null;
  valorCents: number | null;
  emitente: { nome: string | null; documento: string | null };
  destinatario: { nome: string | null; documento: string | null };
};

const PREFIXO = "(?:[A-Za-z0-9_]+:)?";

/** Conteúdo do primeiro elemento com esse nome (com ou sem prefixo de namespace). */
function bloco(xml: string, nome: string): string | null {
  const m = new RegExp(`<${PREFIXO}${nome}(?:\\s[^>]*)?>([\\s\\S]*?)</${PREFIXO}${nome}>`).exec(xml);
  return m ? m[1]! : null;
}

function texto(xml: string | null, ...caminho: string[]): string | null {
  let atual = xml;
  for (const nome of caminho) {
    if (atual == null) return null;
    atual = bloco(atual, nome);
  }
  const v = atual?.replace(/<[^>]+>/g, "").trim();
  return v ? decodificar(v) : null;
}

function decodificar(v: string) {
  return v.replace(/&amp;/g, "&").replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&quot;/g, '"').replace(/&apos;/g, "'");
}

const dia = (v: string | null) => (v && /^\d{4}-\d{2}-\d{2}/.test(v) ? v.slice(0, 10) : null);
const centavos = (v: string | null) => {
  if (!v) return null;
  const n = Number(v.replace(",", "."));
  return Number.isFinite(n) ? Math.round(n * 100) : null;
};
const parte = (xml: string | null) => ({
  nome: texto(xml, "xNome") ?? texto(xml, "RazaoSocial"),
  documento: texto(xml, "CNPJ") ?? texto(xml, "CPF") ?? texto(xml, "Cnpj") ?? texto(xml, "Cpf"),
});

export function lerXmlDeNota(xml: string): DadosDaNota | null {
  const infNFe = /<(?:[A-Za-z0-9_]+:)?infNFe\b[^>]*\bId="NFe(\d{44})"/.exec(xml);
  if (infNFe || bloco(xml, "infNFe")) {
    const ide = bloco(xml, "ide");
    return {
      modelo: "NFE",
      numero: texto(ide, "nNF"),
      serie: texto(ide, "serie"),
      chaveDeAcesso: infNFe?.[1] ?? texto(xml, "chNFe"),
      emitidaEm: dia(texto(ide, "dhEmi") ?? texto(ide, "dEmi")),
      valorCents: centavos(texto(xml, "ICMSTot", "vNF")),
      emitente: parte(bloco(xml, "emit")),
      destinatario: parte(bloco(xml, "dest")),
    };
  }

  const infNFSe = bloco(xml, "infNFSe");
  if (infNFSe) {
    const chave = /<(?:[A-Za-z0-9_]+:)?infNFSe\b[^>]*\bId="NFS(\d+)"/.exec(xml)?.[1] ?? null;
    return {
      modelo: "NFSE",
      numero: texto(infNFSe, "nNFSe"),
      serie: texto(xml, "infDPS", "serie"),
      chaveDeAcesso: chave,
      emitidaEm: dia(texto(xml, "infDPS", "dhEmi") ?? texto(infNFSe, "dhProc")),
      valorCents: centavos(texto(infNFSe, "valores", "vLiq") ?? texto(xml, "vServPrest", "vServ")),
      emitente: parte(bloco(infNFSe, "emit")),
      destinatario: parte(bloco(xml, "toma")),
    };
  }

  const abrasf = bloco(xml, "InfNfse");
  if (abrasf) {
    return {
      modelo: "NFSE",
      numero: texto(abrasf, "Numero"),
      serie: null,
      chaveDeAcesso: texto(abrasf, "CodigoVerificacao"),
      emitidaEm: dia(texto(abrasf, "DataEmissao")),
      valorCents: centavos(texto(abrasf, "ValorLiquidoNfse") ?? texto(abrasf, "ValorServicos")),
      emitente: parte(bloco(abrasf, "PrestadorServico")),
      destinatario: parte(bloco(abrasf, "TomadorServico") ?? bloco(xml, "Tomador")),
    };
  }
  return null;
}
